import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { setItem, LocalLanguage } from '../../../utils/localStorage';
import { Icon } from './Icon';

type LanguageMenuProps = {
  className?: string;
};

/** Draft LanguageMenu.tsx — 드롭다운은 draft-header.css 클래스 */
export function LanguageMenu({ className }: LanguageMenuProps) {
  const { t, i18n } = useTranslation();
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const onPointerDown = (e: MouseEvent) => {
      if (!rootRef.current?.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    window.addEventListener('mousedown', onPointerDown);
    window.addEventListener('keydown', onKey);
    return () => {
      window.removeEventListener('mousedown', onPointerDown);
      window.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const setLanguage = (lng: 'ko' | 'en') => {
    void i18n.changeLanguage(lng);
    setItem(LocalLanguage, lng);
    setOpen(false);
  };

  const current = (i18n.language === 'en' ? 'en' : 'ko') as 'ko' | 'en';

  const defaultBtnClass = 'soundpill-draft-icon-btn soundpill-draft-icon-btn--language';

  return (
    <div ref={rootRef} className="relative soundpill-draft-language-menu">
      <button
        type="button"
        className={className ?? defaultBtnClass}
        aria-label={t('layout.selectLanguage')}
        aria-expanded={open}
        aria-haspopup="listbox"
        onClick={() => setOpen((o) => !o)}
      >
        <Icon name="language" />
      </button>

      {open && (
        <div
          role="listbox"
          aria-label={t('layout.selectLanguage')}
          className="soundpill-draft-language-menu__panel"
        >
          <button
            type="button"
            role="option"
            aria-selected={current === 'ko'}
            onClick={() => setLanguage('ko')}
            className={`soundpill-draft-language-menu__option ${
              current === 'ko' ? 'soundpill-draft-language-menu__option--selected' : ''
            }`}
          >
            {t('layout.localeKo')}
          </button>
          <button
            type="button"
            role="option"
            aria-selected={current === 'en'}
            onClick={() => setLanguage('en')}
            className={`soundpill-draft-language-menu__option ${
              current === 'en' ? 'soundpill-draft-language-menu__option--selected' : ''
            }`}
          >
            {t('layout.localeEn')}
          </button>
        </div>
      )}
    </div>
  );
}
